import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";

import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";

import { ButtonComponent } from "@vg-constellation/angular-17/button";
import { LinkComponent } from "@vg-constellation/angular-17/link";
import { SaveStatusModalComponent } from "@components/save-status-modal/save-status-modal.component";

import { GlobalStateStore } from "@services/global-state/global-state.service";
import { AccountOwnerComponent } from "@components/account-owner/account-owner.component";
import { AccountDetailsComponent } from "@components/account-details/account-details.component";
import { HelpDrawerComponent } from "@components/help-drawer/help-drawer.component";
import { AdobeAnalyticsService } from "@weps/shared-analytics";

import * as constants from "@services/content/content";
import * as GuidedFlowConstants from "@services/content/guided-flow/guided-flow-content";
import { AccountType } from "../../types/AccountType";
import { GUIDED_FLOW_COMPONENT_STEPS } from "@models/guided-flow.map";

import { environment } from "@environments/environment";
import { RobService } from "@services/rob/rob.service";
import { GuidedFlowModalService } from "@services/guided-flow-modal/guided-flow-modal.service";

const GUIDED_FLOW_TITLE_MAP = {
  // WMPS-4245: Theses AccountType should be replaced with InventoryItemType (and the other refs to it in this class)
  [AccountType.BANK]: GuidedFlowConstants.GUIDED_FLOW_ADD_BANK_ACCOUNT,
  [AccountType.RETIREMENT_ANNUITY]:
    GuidedFlowConstants.GUIDED_FLOW_ADD_RETIREMENT_ACCOUNT_OR_ANNUITY,
  [AccountType.INVESTMENT]: GuidedFlowConstants.GUIDED_FLOW_ADD_INVESTMENT_ACCOUNT,
  [AccountType.REAL_ESTATE]: GuidedFlowConstants.GUIDED_FLOW_ADD_REAL_ESTATE,
  [AccountType.MORTGAGE]: GuidedFlowConstants.GUIDED_FLOW_ADD_REAL_ESTATE,
  [AccountType.LIABILITIES]: GuidedFlowConstants.GUIDED_FLOW_ADD_LIABILITY,
  [AccountType.TRUST]: GuidedFlowConstants.GUIDED_FLOW_ADD_TRUST,
};

@Component({
  selector: "app-guided-flow",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    AccountDetailsComponent,
    AccountOwnerComponent,
    HelpDrawerComponent,
    ButtonComponent,
    LinkComponent,
    SaveStatusModalComponent,
  ],
  templateUrl: "./guided-flow.component.html",
  styleUrls: ["./guided-flow.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class GuidedFlowComponent implements AfterViewInit, OnInit {
  readonly globalStore = inject(GlobalStateStore);
  private readonly analyticsService = inject(AdobeAnalyticsService);
  @ViewChild("accountOwner", { static: true }) accountOwnerTemplate: TemplateRef<any>;
  @ViewChild("accountDetails", { static: true }) accountDetailsTemplate: TemplateRef<any>;
  @ViewChild("empty", { static: true }) emptyTemplate: TemplateRef<any>;
  @ViewChild(HelpDrawerComponent) helpDrawer!: HelpDrawerComponent;

  componentSteps = GUIDED_FLOW_COMPONENT_STEPS;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  totalSteps = 0;

  // Determine the type of account, asset, or liability we are adding, should be kebab-case
  // WMPS-4245: This accountType should be replaced with inventoryItemType (and the other refs to it in this class)
  accountType: any;
  ownerType: any;

  guidedFlowTitle = signal<string>("");
  guidedFlowSubtitle = signal<string>("");
  constants = constants;
  // The value of currentStep is defaulted to 1 inside the current-step-service; so don't really need to set a value here, just initialize variable
  currentStep: number;
  isFirstLoad = true;

  @ViewChild("title") titleElRef!: ElementRef;

  // eslint-disable-next-line max-params
  constructor(
    private router: Router,
    public route: ActivatedRoute,
    public robService: RobService,
    private modalService: GuidedFlowModalService,
  ) {}

  ngOnInit(): void {
    const estateProfile = this.globalStore.estateProfile?.();
    const accountTypeState = this.globalStore.getGuidedFlowState("account-type");
    const assetTypeState = this.globalStore.getGuidedFlowState("asset-type");
    this.ownerType = this.globalStore.getGuidedFlowState("owner-type").ownerType;

    // Redirect back to personal wealth inventory page if no estate profile data is found
    if (!estateProfile) {
      this.router.navigate(["/personal-wealth-inventory"]);
      return;
    }

    if (accountTypeState["accountType"]) {
      this.accountType = accountTypeState["accountType"];
    } else if (assetTypeState["accountType"]) {
      this.accountType = assetTypeState["accountType"];
    }

    this.currentStep = this.globalStore.getCurrentStep(this.accountType);
    this.totalSteps = GUIDED_FLOW_COMPONENT_STEPS[this.accountType].length;

    // Fire processStepSuccess for modal category selection (e.g., "bank_accounts").
    // Moved here from add-accounts-assets-dialog to avoid timing race caused by
    // two-hop redirect (consumePendingBeacon consumed by 1st NavigationEnd).
    const state = this.globalStore.getGuidedFlowState(this.accountType);
    const isEdit = state?.isEdit || false;
    if (this.currentStep === 1 && !isEdit) {
      const analyticsValue = this.accountType.replace(/-/g, "_");
      this.analyticsService.processStepSuccess(analyticsValue, "add_to_inventory", analyticsValue);
    }

    this.globalStore.resetGuidedFlowTempState();
  }

  ngAfterViewInit(): void {
    // WMPS-4245: This function should have a more generic name like updateInventoryItemHeader()
    this.updateAccountHeader();
    this.handleRouterNavigate();
  }

  async handleContinue(): Promise<void> {
    const tempState = this.globalStore.getGuidedFlowTempState(this.accountType);

    // Find onSubmit function in temp state or main state, then call it
    const validateOnSubmit =
      tempState["onSubmit"] || this.globalStore.getGuidedFlowState(this.accountType)?.["onSubmit"];
    const errorState = validateOnSubmit ? validateOnSubmit() : true;

    if (errorState) {
      return; // Exit early if validation fails
    }

    const nextStep = this.currentStep + 1;

    if (nextStep <= this.totalSteps) {
      // Navigate to next step
      this.moveToNextStep();
    } else {
      // Submit form on final step
      await this.submitForm();
    }
  }

  private moveToNextStep(): void {
    // Fire processStepSuccess on step 1 (account owner selection) with the owner type value.
    // The category-level processStepSuccess now fires in displayGuidedFlow() (modal selection).
    const state = this.globalStore.getGuidedFlowState(this.accountType);
    const isEdit = state?.isEdit || false;
    if (this.currentStep === 1 && !isEdit) {
      const tempState = this.globalStore.getGuidedFlowTempState(this.accountType);
      const ownerType = (tempState?.selectedAccountOwnerType || "").replace(/-/g, "_");
      this.analyticsService.processStepSuccess(ownerType, "add_to_inventory", ownerType);
    }

    this.globalStore.mergeGuidedFlowTempState(this.accountType);
    this.currentStep += 1;
    this.globalStore.setCurrentStep(this.accountType, this.currentStep);
    this.globalStore.resetGuidedFlowTempState();
    this.handleRouterNavigate();
  }

  private async submitForm(): Promise<void> {
    this.globalStore.mergeGuidedFlowTempState(this.accountType);
    this.globalStore.resetGuidedFlowTempState();

    const state = this.globalStore.getGuidedFlowState(this.accountType);
    this.ownerType = this.globalStore.getGuidedFlowState("owner-type").ownerType;
    const isEdit = state?.isEdit || false;

    const result = await this.modalService.showSaveModal(this.accountType, isEdit, this.ownerType);

    if (result.success) {
      if (isEdit) {
        const editLocation = `edit_${this.accountType.replace(/-/g, "_")}`;
        this.analyticsService.callToAction("confirm", editLocation, "button");
      } else {
        const accountTypeValue = this.accountType.replace(/-/g, "_");
        this.analyticsService.processComplete("add_to_inventory", accountTypeValue);
      }

      // Clean up edit state after successful save
      if (state?.isEdit) {
        const updatedState = { ...state };
        delete updatedState.isEdit;
        this.globalStore.setGuidedFlowInitialState(this.accountType, updatedState);
      }
    }
    // Error handling is managed within the modal service
  }

  handleCancelandQuit(): void {
    const state = this.globalStore.getGuidedFlowState(this.accountType);
    const isEdit = state?.isEdit || false;

    if (isEdit) {
      const editLocation = `edit_${this.accountType.replace(/-/g, "_")}`;
      this.analyticsService.callToAction("quit", editLocation, "button");
    } else {
      this.analyticsService.processAbandoned("add_to_inventory", "close(x)", "quit");
    }

    this.router.navigate([`/personal-wealth-inventory`]);
  }

  handleBack(): void {
    // Check if this is a Vanguard trust in edit mode - if so, act as cancel
    const currentState = this.globalStore.getGuidedFlowState(this.accountType);
    const isEditMode = currentState?.isEdit || false;

    const location = isEditMode
      ? `edit_${this.accountType.replace(/-/g, "_")}`
      : this.guidedFlowTitle().toLowerCase().replace(/\s+/g, "_");
    this.analyticsService.callToAction("back", location, "button");

    const isVanguardTrust =
      this.accountType === AccountType.TRUST && this.globalStore.isVanguard(this.accountType);

    if (isVanguardTrust && isEditMode) {
      this.router.navigate([`/personal-wealth-inventory`]);
    } else {
      // Normal back behavior for other cases
      this.globalStore.resetGuidedFlowTempState();
      // Back button only shows when we are not on the first step, so we don't need a check here
      this.currentStep -= 1;
      this.globalStore.setCurrentStep(this.accountType, this.currentStep);
      this.handleRouterNavigate();
    }
  }

  handleRouterNavigate(): void {
    //this.router.navigate([`./${this.accountType}-${this.currentStep}`], { relativeTo: this.route });
    this.router.navigate([`/guided-flow/${this.accountType}-${this.currentStep}`], {
      relativeTo: this.route,
    });
  }

  handleRouteChange(routeComponent: any): void {
    if (!this.isFirstLoad) {
      this.titleElRef.nativeElement.focus();
      setTimeout(() => {
        // Sync `this.currentStep` based on the current route if not in sync.
        // This works because `routeKey` we pass as data to each route contains current step number.
        // Alternatives can be using queryParam of currentStep and subscriptions.
        const currentStep = parseInt(routeComponent.routeKey.split("-").pop());
        if (currentStep !== this.currentStep) {
          this.currentStep = currentStep;
          this.globalStore.setCurrentStep(this.accountType, this.currentStep);
        }
      });
    }

    this.isFirstLoad = false;
  }

  // WMPS-4245: This function should have a more generic name like updateInventoryItemHeader()
  updateAccountHeader(): void {
    // This will be static for now, but MIGHT be dynamic in the future
    this.guidedFlowSubtitle.set(constants.GUIDED_FLOW_ENTER_TITLING_AND_DETAILS);

    const state = this.globalStore.getGuidedFlowState(this.accountType);
    const title = state?.isEdit
      ? GuidedFlowConstants.GUIDED_FLOW_EDIT_PREFIX +
        GUIDED_FLOW_TITLE_MAP[this.accountType || AccountType.INVESTMENT]
      : GuidedFlowConstants.GUIDED_FLOW_ADD_PREFIX +
        GUIDED_FLOW_TITLE_MAP[this.accountType || AccountType.INVESTMENT];

    this.guidedFlowTitle.set(title);
  }

  getTemplateForStep(step: string): TemplateRef<any> {
    switch (step) {
      case "account-owner":
        return this.accountOwnerTemplate;
      case "account-details":
        return this.accountDetailsTemplate;
      default:
        return this.emptyTemplate;
    }
  }

  triggerHelpDrawer(): void {
    this.helpDrawer.handleDrawerState();
  }

  triggerBeneficiaryHelpDrawer(): void {
    this.helpDrawer.handleDrawerState("beneficiaries");
  }

  protected readonly environment = environment;
  // WMPS-4245: This accountType should be replaced with inventoryItemType (and the other refs to it in this class)
  protected readonly AccountType = AccountType;
}
